import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import MapFieldDrawer from '../components/MapFieldDrawer';
import { Plus, MapPin, Trash2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Fields = () => {
  const { getAuthHeaders } = useAuth();
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddField, setShowAddField] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [formData, setFormData] = useState({
    name: '', crop: '', sowing_date: '', coordinates: '',
    initial_stage: null, geometry: null, previous_crop: ''
  });
  const [showStageSelection, setShowStageSelection] = useState(false);
  const [cropStages, setCropStages] = useState([]);
  const [showMapDrawer, setShowMapDrawer] = useState(false);

  useEffect(() => { loadFields(); }, []);

  const loadFields = async () => {
    try {
      const response = await axios.get(`${API}/fields`, { headers: getAuthHeaders() });
      setFields(response.data);
    } catch (error) { toast.error('Failed to load fields'); }
    finally { setLoading(false); }
  };

  const handleDeleteField = async (fieldId, fieldName) => {
    try {
      await axios.delete(`${API}/fields/${fieldId}`, { headers: getAuthHeaders() });
      toast.success(`Field "${fieldName}" deleted`);
      setShowDeleteConfirm(null);
      loadFields();
    } catch (error) { toast.error('Failed to delete field'); }
  };

  const handleSowingDateChange = (date) => {
    setFormData({ ...formData, sowing_date: date });
    const today = new Date().toISOString().split('T')[0];
    if (date !== today && formData.crop) {
      loadCropStages(formData.crop);
      setShowStageSelection(true);
    } else {
      setShowStageSelection(false);
      setFormData(prev => ({ ...prev, initial_stage: null }));
    }
  };

  const loadCropStages = (crop) => {
    const stagesByCrop = {
      wheat_winter: ['emergence','tillering','stem elongation','booting','heading/flowering','grain fill','maturity'],
      wheat_spring: ['emergence','tillering','stem elongation','booting','heading/flowering','grain fill','maturity'],
      barley_winter: ['emergence','tillering','stem elongation','heading/flowering','grain fill','maturity'],
      barley_spring: ['emergence','tillering','stem elongation','heading/flowering','grain fill','maturity'],
      oats: ['emergence','tillering','stem elongation','heading/flowering','grain fill','maturity'],
      corn: ['emergence','V6','V12','tassel/silk','grain fill','maturity'],
      sunflower: ['emergence','vegetative','bud','flowering','seed fill','maturity'],
      rapeseed: ['emergence','rosette','stem extension','flowering','pod fill','maturity'],
      potatoes: ['sprout/emerge','vegetative','tuber initiation','tuber bulking','maturity'],
      sorghum: ['emergence','vegetative','boot','heading/flowering','grain fill','maturity'],
      peas: ['emergence','vegetative','flowering','pod fill','maturity'],
      chickpeas: ['emergence','vegetative','flowering','pod fill','maturity'],
      lentils: ['emergence','vegetative','flowering','pod fill','maturity'],
      alfalfa: ['green-up','vegetative','bud','flower'],
    };
    setCropStages(stagesByCrop[crop] || []);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const today = new Date().toISOString().split('T')[0];
    if (formData.crop !== 'no_current_crop' && formData.sowing_date !== today && !formData.initial_stage && cropStages.length > 0) {
      toast.error('Please select the current crop stage');
      setShowStageSelection(true); return;
    }
    let geometry = formData.geometry;
    if (!geometry && formData.coordinates) {
      try {
        const coordPairs = formData.coordinates.split(';').map(pair => {
          const [lng, lat] = pair.trim().split(',').map(Number);
          return [lng, lat];
        });
        coordPairs.push(coordPairs[0]);
        geometry = { type: 'Polygon', coordinates: [coordPairs] };
      } catch (error) { toast.error('Invalid coordinates format'); return; }
    }
    if (!geometry) { toast.error('Please draw field on map or enter coordinates'); return; }

    try {
      const fieldData = { name: formData.name, crop: formData.crop, geometry, previous_crop: formData.previous_crop || null };
      if (formData.crop !== 'no_current_crop') {
        fieldData.sowing_date = formData.sowing_date;
        fieldData.initial_stage = formData.initial_stage;
      }
      await axios.post(`${API}/fields`, fieldData, { headers: getAuthHeaders() });
      toast.success('Field created successfully!');
      setShowAddField(false);
      setFormData({ name: '', crop: '', sowing_date: '', coordinates: '', initial_stage: null, geometry: null, previous_crop: '' });
      setShowStageSelection(false); setShowMapDrawer(false);
      loadFields();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to create field'); }
  };

  const handleFieldDrawn = (geometry) => {
    setFormData({ ...formData, geometry });
    setShowMapDrawer(false);
    toast.success('Field boundary saved! Complete the form to create field.');
  };

  if (loading) {
    return <Layout><div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 384 }}><div className="spinner" /></div></Layout>;
  }

  return (
    <Layout>
      <div data-testid="fields-page" style={{ maxWidth: 1200 }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h1 style={{ fontSize: 24, fontWeight: 600 }}>Fields</h1>
          <button className="btn-depth-primary" onClick={() => setShowAddField(true)} data-testid="add-field-button">
            <Plus size={18} /> Add Field
          </button>
        </div>

        {fields.length === 0 ? (
          <section className="card-floating" style={{ position: 'relative', padding: 44, textAlign: 'center' }}>
            <MapPin size={48} style={{ margin: '0 auto 14px', color: 'var(--text-muted)' }} />
            <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 6 }}>No Fields Yet</h2>
            <p style={{ color: 'var(--text-muted)', marginBottom: 20, fontSize: 13 }}>Create your first field to start monitoring crops and weather.</p>
            <button className="btn-depth-primary" onClick={() => setShowAddField(true)}>Create First Field</button>
          </section>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
            {fields.map(field => {
              const isHarvested = !field.crop || field.stage === 'harvested';
              return (
                <section key={field.id} className="card-floating" style={{ position: 'relative', padding: 18 }} data-testid={`field-item-${field.id}`}>
                  <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowDeleteConfirm(field); }}
                    style={{ position: 'absolute', top: 10, right: 10, padding: 6, background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', borderRadius: 8 }}
                    title="Delete field" data-testid={`delete-field-${field.id}`}>
                    <Trash2 size={15} />
                  </button>

                  <a href={`/fields/${field.id}`} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
                    <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4, paddingRight: 28 }}>{field.name}</h3>
                    {isHarvested ? (
                      <div style={{ marginTop: 10 }}>
                        <span className="badge-depth badge-warning" style={{ fontSize: 11 }}>
                          <AlertCircle size={12} /> Harvested - No Active Crop
                        </span>
                        {field.harvest_date && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginTop: 8 }}>
                            <span style={{ color: 'var(--text-muted)' }}>Last Harvest:</span>
                            <span style={{ fontWeight: 500 }}>{new Date(field.harvest_date).toLocaleDateString()}</span>
                          </div>
                        )}
                        <p style={{ fontSize: 12, color: 'var(--warning)', marginTop: 8 }}>Click to add new crop</p>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 10, fontSize: 12 }}>
                        {[
                          ['Crop', field.crop],
                          ['Sowing Date', field.sowing_date ? new Date(field.sowing_date).toLocaleDateString() : 'N/A'],
                          ['GDD', <span style={{ color: 'var(--gold)', fontWeight: 600 }}>{(field.gdd || 0).toFixed(1)}</span>],
                          ['Rain', <span style={{ color: 'var(--info)', fontWeight: 600 }}>
                            {(field.rain_accumulation || 0).toFixed(1)} mm
                            {field.sowing_date && <span style={{ fontWeight: 400, fontSize: 10, color: 'var(--text-muted)', marginLeft: 4 }}>
                              (since {new Date(field.sowing_date).toLocaleDateString()})
                            </span>}
                          </span>],
                        ].map(([lbl, val]) => (
                          <div key={lbl} style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: 'var(--text-muted)' }}>{lbl}:</span>
                            <span style={{ fontWeight: 500 }}>{val}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </a>
                </section>
              );
            })}
          </div>
        )}

        {/* Delete Confirmation */}
        {showDeleteConfirm && (
          <div className="modal-overlay" onClick={() => setShowDeleteConfirm(null)}>
            <div className="card-floating anim-scale" style={{ position: 'relative', maxWidth: 420, width: '100%', padding: 22 }} onClick={(e) => e.stopPropagation()}>
              <h2 style={{ fontSize: 18, fontWeight: 600, color: 'var(--danger)', marginBottom: 10 }}>Delete Field?</h2>
              <p style={{ color: 'var(--text-secondary)', marginBottom: 6, fontSize: 13 }}>
                Are you sure you want to permanently delete <strong>"{showDeleteConfirm.name}"</strong>?
              </p>
              <p style={{ fontSize: 12, color: 'var(--danger)', marginBottom: 18 }}>
                This will delete ALL data including the field boundary, weather history, NDVI readings, and alerts. This action cannot be undone.
              </p>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn-depth-danger" style={{ flex: 1 }} onClick={() => handleDeleteField(showDeleteConfirm.id, showDeleteConfirm.name)}
                  data-testid="confirm-delete-button">Delete Permanently</button>
                <button className="btn-depth-ghost" style={{ flex: 1 }} onClick={() => setShowDeleteConfirm(null)}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* Map Drawer */}
        {showMapDrawer && (
          <div className="modal-overlay" onClick={() => setShowMapDrawer(false)}>
            <div className="card-floating" style={{ position: 'relative', maxWidth: 1100, width: '100%', maxHeight: '90vh', padding: 22, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
              onClick={(e) => e.stopPropagation()}>
              <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 14, flexShrink: 0 }}>Draw Field Boundary</h2>
              <div style={{ flex: 1, overflow: 'hidden', borderRadius: 12 }}>
                <MapFieldDrawer onFieldCreated={handleFieldDrawn} onCancel={() => setShowMapDrawer(false)} />
              </div>
            </div>
          </div>
        )}

        {/* Add Field Modal */}
        {showAddField && !showMapDrawer && (
          <div className="modal-overlay" onClick={() => setShowAddField(false)}>
            <div className="card-floating anim-scale" style={{ position: 'relative', maxWidth: 560, width: '100%', padding: 24, maxHeight: '90vh', overflowY: 'auto' }}
              onClick={(e) => e.stopPropagation()} data-testid="add-field-modal">
              <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 18 }}>Add New Field</h2>
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div><label className="label-depth">Field Name</label>
                  <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="input-depth" required data-testid="field-name-input" /></div>

                <div><label className="label-depth">Crop Type</label>
                  <select value={formData.crop} onChange={(e) => { setFormData({ ...formData, crop: e.target.value });
                    if (e.target.value !== 'no_current_crop' && formData.sowing_date !== new Date().toISOString().split('T')[0]) loadCropStages(e.target.value); }}
                    className="input-depth" required data-testid="crop-select">
                    <option value="">Select crop</option>
                    <option value="no_current_crop">No current crop</option>
                    <optgroup label="Wheat"><option value="wheat_winter">Wheat (Winter)</option><option value="wheat_spring">Wheat (Spring)</option></optgroup>
                    <optgroup label="Barley"><option value="barley_winter">Barley (Winter)</option><option value="barley_spring">Barley (Spring)</option></optgroup>
                    <option value="oats">Oats</option><option value="corn">Corn / Maize</option><option value="sunflower">Sunflower</option>
                    <option value="rapeseed">Rapeseed / Canola</option><option value="potatoes">Potatoes</option><option value="sorghum">Sorghum</option>
                    <optgroup label="Pulses"><option value="peas">Peas</option><option value="chickpeas">Chickpeas</option><option value="lentils">Lentils</option></optgroup>
                    <option value="alfalfa">Alfalfa / Lucerne</option>
                  </select></div>

                <div><label className="label-depth">Previous Crop (optional)</label>
                  <select value={formData.previous_crop || ''} onChange={(e) => setFormData({ ...formData, previous_crop: e.target.value })}
                    className="input-depth" data-testid="previous-crop-select">
                    <option value="">Select previous crop</option>
                    {['wheat','barley','oats','corn','sunflower','rapeseed','potatoes','sorghum','peas','chickpeas','lentils','alfalfa','fallow','other'].map(c => (
                      <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                    ))}
                  </select></div>

                {formData.crop !== 'no_current_crop' && (
                  <div><label className="label-depth">Sowing Date</label>
                    <input type="date" value={formData.sowing_date} onChange={(e) => handleSowingDateChange(e.target.value)}
                      className="input-depth" required data-testid="sowing-date-input" />
                    <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Harvest date will be recorded when you harvest the field</p></div>
                )}

                {formData.crop !== 'no_current_crop' && showStageSelection && cropStages.length > 0 && (
                  <div style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 12, padding: 14 }}>
                    <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--warning)', marginBottom: 6 }}>
                      Field created after sowing date - Select current crop stage:</p>
                    <select value={formData.initial_stage || ''} onChange={(e) => setFormData({ ...formData, initial_stage: e.target.value })}
                      className="input-depth" required data-testid="stage-select">
                      <option value="">Select current stage</option>
                      {cropStages.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                )}

                <div><label className="label-depth">Field Boundary</label>
                  {!formData.geometry ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <button type="button" onClick={() => setShowMapDrawer(true)} className="btn-depth-primary" style={{ width: '100%' }}
                        data-testid="draw-on-map-button">Draw on Map or Upload File</button>
                      <div style={{ textAlign: 'center', fontSize: 12, color: 'var(--text-muted)' }}>or</div>
                      <textarea value={formData.coordinates} onChange={(e) => setFormData({ ...formData, coordinates: e.target.value })}
                        placeholder="Enter coordinates as: lon1,lat1;lon2,lat2;lon3,lat3" className="input-depth" rows={3} />
                      <p style={{ fontSize: 11, color: 'var(--text-muted)' }}>Example: -95.4,29.7;-95.3,29.7;-95.3,29.8;-95.4,29.8</p>
                    </div>
                  ) : (
                    <div style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 12, padding: 14 }}>
                      <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--success)' }}>Field boundary saved ({formData.geometry.coordinates[0].length - 1} points)</p>
                      <button type="button" onClick={() => { setFormData({ ...formData, geometry: null }); setShowMapDrawer(true); }}
                        style={{ fontSize: 12, color: 'var(--gold)', textDecoration: 'underline', marginTop: 6, background: 'none', border: 'none', cursor: 'pointer' }}>Redraw</button>
                    </div>
                  )}</div>

                <div style={{ display: 'flex', gap: 10, paddingTop: 10 }}>
                  <button type="submit" className="btn-depth-primary" style={{ flex: 1 }} data-testid="submit-field-button">Create Field</button>
                  <button type="button" className="btn-depth-ghost" style={{ flex: 1 }} onClick={() => setShowAddField(false)}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Fields;
